import { observer } from 'mobx-react-lite';
import { MainStore } from '../../stores/MainStore';
import React, { useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { CopilotKnowledgeArticles } from './widgets/CopilotKnowledgeArticles';
import { CopilotCustomPrompts } from './widgets/custom prompts/CopilotCustomPrompts';
import { CopilotConfig } from './widgets/CopilotConfig';
import { CopilotCaching } from './widgets/CopilotCaching';
import { ConfigMigration } from './widgets/ConfigMigration';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { SelectionItem, SIAutocomplete } from '../controls/Autocomplete';
import WidgetsIcon from '@mui/icons-material/Widgets';
import BackIcon from '@mui/icons-material/ArrowBack';
import { StorageMigration } from './widgets/StorageMigration';
import { CustomerSuggestions } from './widgets/CustomerSuggestions';
import { TagManagement } from './widgets/tag management/TagManagement';
import { PartsManagement } from './widgets/PartsManagement';
// Fun widget icons
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PeopleIcon from '@mui/icons-material/People';
import DatabaseIcon from '@mui/icons-material/Storage';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ImageIcon from '@mui/icons-material/Image';

const groupsWithSupportAccess = ['SID Support', 'SID Admin', 'Aquant QA']
const groupsWithAdminAccess = ['SID Admin', 'Aquant QA']

type CopilotMainProps = {
    mainStore: MainStore
}

export const CopilotMain = observer(({ mainStore }: CopilotMainProps) => {
    const copilotStore = mainStore.copilotStore!

    const {
        customers, 
        selectedCustomer: customer,
        setSelectedCustomer,
        setSelectedModel
    } = copilotStore

    const { authState, oktaAuth } = useOktaAuth();
    const [userGroups, setUserGroups] = React.useState<string[]>([]);
    const [selectedWidgetIndex, setSelectedWidgetIndex] = React.useState<number | undefined>(undefined);
    
    useEffect(() => {
        if (!authState || !authState.isAuthenticated) setUserGroups([]);
        else oktaAuth.getUser().then(user => { setUserGroups(user.groups as string[]) })
    }, [authState, oktaAuth]);

    type Widget = {
        name: string,
        page: JSX.Element
        icon?: JSX.Element
        approvedGroups?: string[]
    }

    const unlockPdfReadMethods = groupsWithSupportAccess.some(group => userGroups.includes(group))

    const widgets: Widget[] = [
        {
            name: 'Knowledge Articles', 
            page: <CopilotKnowledgeArticles store={copilotStore} uploadToPresignedUrl={mainStore.uploadToPresignedUrl}/>,
            icon: <ArticleIcon fontSize="large" />
        },
        {
            name: 'Tag Management',
            page: <TagManagement store={copilotStore} />,
            approvedGroups: groupsWithSupportAccess,
            icon: <LocalOfferIcon fontSize="large" />
        },
        {
            name: 'Config', 
            page: <CopilotConfig store={copilotStore} unlockPdfReadMethods={unlockPdfReadMethods} />,
            icon: <SettingsIcon fontSize="large" />
        },
        {
            name: 'Config Migration', 
            page: <ConfigMigration store={copilotStore} unlockPdfReadMethods={unlockPdfReadMethods}/>,
            approvedGroups: groupsWithSupportAccess,
            icon: <SwapHorizIcon fontSize="large" />
        },
        {
            name: 'Caching', 
            page: <CopilotCaching store={copilotStore}/>,
            icon: <DatabaseIcon fontSize="large" />
        },
        {
            name: 'Custom Prompts', 
            page: <CopilotCustomPrompts store={copilotStore} />, 
            approvedGroups: groupsWithSupportAccess,
            icon: <SmartToyIcon fontSize="large" />
        },
        {
            name: 'Customer Suggestions',
            page: <CustomerSuggestions store={copilotStore} />,
            icon: <PeopleIcon fontSize="large" />
        },
        {
            name: 'Parts Management',
            page: <PartsManagement store={copilotStore} />,
            approvedGroups: groupsWithSupportAccess,
            icon: <ImageIcon fontSize="large" />
        },
        {
            name: 'Storage Migration', 
            page: <StorageMigration store={copilotStore} />,
            approvedGroups: groupsWithAdminAccess,
            icon: <DriveFileMoveIcon fontSize="large" />
        }
    ]

    const approved = (widget: Widget) => {
        if (!widget.approvedGroups) return true
        return widget.approvedGroups.some(group => userGroups.includes(group))
    }

    const approvedWidgets = widgets.filter(w => approved(w))

    useEffect(() => {
        setSelectedModel(undefined)
    }, [customer, selectedWidgetIndex])

    return (
        <Stack style={{ height: 'calc(100vh - 70px)', overflow: 'hidden', padding: '24px'}}>
            <Paper elevation={1} sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, mb: 2, padding: 2}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" height={'48px'} >
                    <Stack direction="row" alignItems="center" spacing={2} flex={2}>
                        <Typography style={{fontWeight: 500}}>Customer:</Typography>
                        <SIAutocomplete
                            items={customers.slice().sort((c1, c2) => c1.name.localeCompare(c2.name)).map(tc => {return {value: tc.id, label: tc.name}})}
                            initial_selection={customer ? {value: customer.id, label: customer.name} : undefined} 
                            onSelect={(item?: SelectionItem) => setSelectedCustomer(item?.value)}
                            style={{ width: '100%' }}
                            loadProgress={() => true}
                            placeholder='Select Customer'
                            variant='standard'
                        />
                    </Stack>
                    {selectedWidgetIndex !== undefined && <>
                    <Stack flex={1} justifyContent={"center"} alignItems="center">
                        <Typography variant='h6'>{approvedWidgets[selectedWidgetIndex].name}</Typography>
                    </Stack>
                    <Stack flex={2} direction="row" justifyContent="flex-end" alignItems="center">
                        <IconButton onClick={()=>setSelectedWidgetIndex(undefined)} >
                            <BackIcon />
                        </IconButton>
                    </Stack>
                    </>
                    }
                </Stack>
            </Paper>
            <Paper
                elevation={1}
                sx={{
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: 1,
                    mb: 0,
                    padding: 2,
                    flexGrow: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {selectedWidgetIndex !== undefined && (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {approvedWidgets[selectedWidgetIndex].page}
                    </Box>
                )}
                {selectedWidgetIndex === undefined &&
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2}}>
                    {widgets.filter(w => approved(w)).map((widget, index) => (
                        <Box key={index}
                        onClick={() => setSelectedWidgetIndex(index)}
                        sx={{
                        padding: 2,  
                        gap: 1,
                        width: 140,
                        height: 140,
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: selectedWidgetIndex === index ? '#a7c3ff' : '#e9f0ff',
                        '&:hover': {
                            backgroundColor: '#a7c3ff',
                        },
                        }}
                        >
                    {widget.icon || <WidgetsIcon fontSize="large" />}
                    <Typography align="center">
                        {widget.name}
                    </Typography>
                </Box>
                    ))}
                </Box>
                }
            </Paper>
        </Stack>)
})